import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Building2, Plus, Pencil, Trash2, Search, MapPin, FileText, Upload, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DatePicker from "react-multi-date-picker";
import arabic from "react-date-object/calendars/arabic";
import arabic_ar from "react-date-object/locales/arabic_ar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const RealEstatePage = () => {
  const { token, isAdmin } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [deedFile, setDeedFile] = useState(null);
  const [registrationFile, setRegistrationFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    deed_number: '',
    deed_date: '',
    real_estate_registration: 'not_registered',
    is_mortgaged: false,
    mortgage_details: '',
    address: '',
    area: '',
    notes: '',
    deed_file_name: '',
    registration_file_name: ''
  });

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/real-estate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let propertyId;
      if (selectedProperty) {
        await axios.put(`${API}/real-estate/${selectedProperty.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        propertyId = selectedProperty.id;
        toast.success('تم تحديث العقار بنجاح');
      } else {
        const response = await axios.post(`${API}/real-estate`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        propertyId = response.data.id;
        toast.success('تم إضافة العقار بنجاح');
      }
      
      // Upload PDF if selected
      if (deedFile && propertyId) {
        const formDataFile = new FormData();
        formDataFile.append('file', deedFile);
        await axios.post(`${API}/real-estate/${propertyId}/upload-deed`, formDataFile, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('تم رفع ملف الصك بنجاح');
      }
      
      // Upload registration file if selected
      if (registrationFile && propertyId) {
        const formDataFile = new FormData();
        formDataFile.append('file', registrationFile);
        await axios.post(`${API}/real-estate/${propertyId}/upload-registration`, formDataFile, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('تم رفع ملف التسجيل العيني بنجاح');
      }
      
      setIsDialogOpen(false);
      fetchProperties();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/real-estate/${selectedProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف العقار بنجاح');
      setIsDeleteDialogOpen(false);
      fetchProperties();
    } catch (error) {
      toast.error('خطأ في حذف العقار');
    }
  };
  
  const handleViewDeed = async (propertyId) => {
    try {
      const response = await axios.get(`${API}/real-estate/${propertyId}/deed`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تحميل الملف');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      deed_number: '',
      deed_date: '',
      real_estate_registration: 'not_registered',
      is_mortgaged: false,
      mortgage_details: '',
      address: '',
      area: '',
      notes: '',
      deed_file_name: '',
      registration_file_name: ''
    });
    setDeedFile(null);
    setRegistrationFile(null);
    setSelectedProperty(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setDeedFile(file);
      setFormData({ ...formData, deed_file_name: file.name });
      toast.success('تم اختيار الملف بنجاح');
    } else {
      toast.error('يرجى اختيار ملف PDF');
    }
  };

  const handleRegistrationFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setRegistrationFile(file);
      setFormData({ ...formData, registration_file_name: file.name });
      toast.success('تم اختيار ملف التسجيل العيني بنجاح');
    } else {
      toast.error('يرجى اختيار ملف PDF');
    }
  };

  const openEditDialog = (property) => {
    setSelectedProperty(property);
    setFormData({
      title: property.title,
      deed_number: property.deed_number,
      deed_date: property.deed_date,
      real_estate_registration: property.real_estate_registration || 'not_registered',
      is_mortgaged: property.is_mortgaged,
      mortgage_details: property.mortgage_details || '',
      address: property.address,
      area: property.area || '',
      notes: property.notes || '',
      deed_file_name: property.deed_file_name || '',
      registration_file_name: property.registration_file_name || ''
    });
    setIsDialogOpen(true);
  };

  const openViewDialog = (property) => {
    setSelectedProperty(property);
    setIsViewDialogOpen(true);
  };

  const filteredProperties = properties.filter(property =>
    (property.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (property.deed_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (property.address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="real-estate-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">الصكوك العقارية</h1>
          <p className="text-slate-500 mt-1">إدارة جميع الصكوك العقارية والممتلكات</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="text-white" style={{ background: '#1a365d' }}
            data-testid="add-property-btn"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة عقار
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="البحث عن عقار..."
          className="pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-properties"
        />
      </div>

      {/* Properties Table */}
      <Card className="border-0 shadow-sm" data-testid="properties-table-card">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {isAdmin && <TableHead className="text-center w-24">إجراءات</TableHead>}
                  <TableHead className="text-center">استعراض</TableHead>
                  <TableHead className="text-center">ملف الصك</TableHead>
                  <TableHead className="text-center">حالة الرهن</TableHead>
                  <TableHead className="text-center">التسجيل العيني</TableHead>
                  <TableHead className="text-center">تاريخ الصك</TableHead>
                  <TableHead className="text-center">رقم الصك</TableHead>
                  <TableHead className="text-right">العقار</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-slate-400">
                      <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>لا توجد عقارات</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProperties.map((property) => (
                    <TableRow key={property.id} className="table-row-hover" data-testid={`property-row-${property.id}`}>
                      {isAdmin && (
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(property)}
                              data-testid={`edit-property-${property.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => { setSelectedProperty(property); setIsDeleteDialogOpen(true); }}
                              data-testid={`delete-property-${property.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700"
                          onClick={() => openViewDialog(property)}
                          data-testid={`view-property-${property.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        {property.deed_pdf_path ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => handleViewDeed(property.id)}
                            data-testid={`view-deed-${property.id}`}
                          >
                            <FileText className="w-4 h-4 ml-1" />
                            عرض
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">لا يوجد</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {property.is_mortgaged ? (
                          <Badge variant="destructive">مرهون</Badge>
                        ) : (
                          <Badge className="bg-emerald-500">غير مرهون</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {property.real_estate_registration === 'registered' ? 'تم تسجيله' : 'لم يسجل'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="number text-sm">{property.deed_date}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium number">{property.deed_number}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-3 justify-end">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{property.title}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1 justify-end">
                              <MapPin className="w-3 h-3" />
                              {property.address}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-emerald-600" />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl" dir="rtl">
          <DialogHeader>
            <DialogTitle>{selectedProperty ? 'تعديل العقار' : 'إضافة عقار جديد'}</DialogTitle>
            <DialogDescription>أدخل بيانات العقار بالكامل</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>اسم العقار</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="فيلا الرياض..."
                  required
                  data-testid="property-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الصك</Label>
                <Input
                  value={formData.deed_number}
                  onChange={(e) => setFormData({ ...formData, deed_number: e.target.value })}
                  required
                  data-testid="property-deed-input"
                />
              </div>
              <div className="space-y-2">
                <Label>تاريخ الصك (هجري)</Label>
                <DatePicker
                  calendar={arabic}
                  locale={arabic_ar}
                  value={formData.deed_date}
                  onChange={(date) => setFormData({ ...formData, deed_date: date?.format("YYYY/MM/DD") || '' })}
                  format="YYYY/MM/DD"
                  inputClass="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-right"
                  containerClassName="w-full"
                  calendarPosition="bottom-right"
                  data-testid="property-deed-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>التسجيل العيني</Label>
                <Select
                  value={formData.real_estate_registration}
                  onValueChange={(value) => setFormData({ ...formData, real_estate_registration: value })}
                >
                  <SelectTrigger data-testid="property-registration-select">
                    <SelectValue placeholder="اختر حالة التسجيل" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registered">تم تسجيله</SelectItem>
                    <SelectItem value="not_registered">لم يسجل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>العنوان</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="المدينة، الحي..."
                  required
                  data-testid="property-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>المساحة</Label>
                <Input
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="500 م²"
                  data-testid="property-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label>حالة الرهن</Label>
                <div className="flex items-center gap-3 pt-2">
                  <Switch
                    checked={formData.is_mortgaged}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_mortgaged: checked })}
                    data-testid="property-mortgage-switch"
                  />
                  <span className="text-sm">{formData.is_mortgaged ? 'مرهون' : 'غير مرهون'}</span>
                </div>
              </div>
              {formData.is_mortgaged && (
                <div className="space-y-2 col-span-2">
                  <Label>تفاصيل الرهن</Label>
                  <Input
                    value={formData.mortgage_details}
                    onChange={(e) => setFormData({ ...formData, mortgage_details: e.target.value })}
                    placeholder="اسم البنك، قيمة الرهن..."
                    data-testid="property-mortgage-details-input"
                  />
                </div>
              )}
              <div className="space-y-2 col-span-3">
                <Label>ملف الصك (PDF)</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-slate-300 transition-colors">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    id="deed-file"
                    data-testid="property-deed-file-input"
                  />
                  <label htmlFor="deed-file" className="cursor-pointer">
                    {formData.deed_file_name ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-600">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm">{formData.deed_file_name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm">اضغط لاختيار ملف PDF</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div className="space-y-2 col-span-3">
                <Label>ملف التسجيل العيني (PDF)</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-slate-300 transition-colors">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleRegistrationFileChange}
                    className="hidden"
                    id="registration-file"
                    data-testid="property-registration-file-input"
                  />
                  <label htmlFor="registration-file" className="cursor-pointer">
                    {formData.registration_file_name ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-600">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm">{formData.registration_file_name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm">اضغط لاختيار ملف PDF</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div className="space-y-2 col-span-3">
                <Label>ملاحظات</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="ملاحظات إضافية..."
                  data-testid="property-notes-input"
                />
              </div>
            </div>
            <DialogFooter className="flex gap-3 mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="min-w-[100px]">
                إلغاء
              </Button>
              <Button type="submit" className="text-white min-w-[120px]" style={{ background: '#1a365d' }} data-testid="save-property-btn">
                {selectedProperty ? 'حفظ التعديلات' : 'إضافة العقار'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle className="text-right">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل أنت متأكد من حذف هذا العقار؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-property"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Property Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: '#10B981' }} />
              تفاصيل العقار
            </DialogTitle>
          </DialogHeader>
          {selectedProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">اسم العقار</p>
                  <p className="font-medium" style={{ color: '#1a365d' }}>{selectedProperty.title}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">العنوان</p>
                  <p className="font-medium">{selectedProperty.address}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">رقم الصك</p>
                  <p className="font-medium number">{selectedProperty.deed_number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">تاريخ الصك</p>
                  <p className="font-medium number">{selectedProperty.deed_date}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">التسجيل العيني</p>
                  <p className="font-medium">
                    {selectedProperty.real_estate_registration === 'registered' ? 'تم تسجيله' : 'لم يسجل'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">حالة الرهن</p>
                  <p className="font-medium">
                    {selectedProperty.is_mortgaged ? 'مرهون' : 'غير مرهون'}
                  </p>
                </div>
                {selectedProperty.area && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">المساحة</p>
                    <p className="font-medium">{selectedProperty.area}</p>
                  </div>
                )}
                {selectedProperty.mortgage_details && (
                  <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-sm text-slate-500">تفاصيل الرهن</p>
                    <p className="font-medium">{selectedProperty.mortgage_details}</p>
                  </div>
                )}
                {selectedProperty.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-sm text-slate-500">ملاحظات</p>
                    <p className="font-medium">{selectedProperty.notes}</p>
                  </div>
                )}
              </div>
              {selectedProperty.deed_pdf_path && (
                <Button
                  onClick={() => handleViewDeed(selectedProperty.id)}
                  className="w-full text-white"
                  style={{ background: '#1a365d' }}
                >
                  <FileText className="w-4 h-4 ml-2" />
                  عرض ملف الصك
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RealEstatePage;
